import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import { toast } from "react-toastify";
import "../styles/PanelSizing.css";

// Static data moved outside component
const panelOptions = [100, 150, 200, 250, 300, 350, 370, 400, 420, 450, 500, 550, 600];
const batteryCountOptions = Array.from({ length: 20 }, (_, i) => i + 1);
const lithiumKwhOptions = [5, 10, 15, 20];
const totalCards = 3;

// Memoized Tooltip component
const Tooltip = memo(({ text }) => (
  <span
    style={{
      marginLeft: 6,
      cursor: "pointer",
      color: "#0077cc",
      fontWeight: "bold",
    }}
    title={text}
  >
    ⓘ
  </span>
));

export default function PanelSizing() {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("battery");
  const [batteryCount, setBatteryCount] = useState("");
  const [batteryVoltage, setBatteryVoltage] = useState("");
  const [batteryAh, setBatteryAh] = useState("");
  const [batteryKwh, setBatteryKwh] = useState("");
  const [batteryType, setBatteryType] = useState("lead");
  const [autonomyChoice, setAutonomyChoice] = useState("");
  const [customAutonomyDays, setCustomAutonomyDays] = useState("");
  const [appliances, setAppliances] = useState([{ name: "", watt: "", hours: "", qty: "" }]);
  const [psh, setPsh] = useState("");
  const [panelWatt, setPanelWatt] = useState("");
  const [includeLoss, setIncludeLoss] = useState(true);
  const [result, setResult] = useState(null);
  const [calcCount, setCalcCount] = useState(0);
  const [cardIndex, setCardIndex] = useState(0);
  const carouselRef = useRef(null);

  // Combined carousel effect with deferred start
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const interval = setInterval(() => {
        setCardIndex((prev) => {
          const next = (prev + 1) % totalCards;
          if (carouselRef.current) {
            const containerWidth = carouselRef.current.offsetWidth;
            carouselRef.current.scrollTo({
              left: next * containerWidth,
              behavior: "smooth",
            });
          }
          return next;
        });
      }, 5000);
      return () => clearInterval(interval);
    }, 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  // Scroll on manual cardIndex change
  useEffect(() => {
    if (carouselRef.current) {
      const containerWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: cardIndex * containerWidth,
        behavior: "smooth",
      });
    }
  }, [cardIndex]);

  // Memoized running total
  const runningTotalLoad = useMemo(() => {
    return appliances.reduce((sum, a) => {
      const watt = parseFloat(a.watt) || 0;
      const hours = parseFloat(a.hours) || 0;
      const qty = parseFloat(a.qty) || 0;
      return sum + watt * hours * qty;
    }, 0);
  }, [appliances]);

  // Memoized handlers
  const updateAppliance = useCallback((index, field, value) => {
    setAppliances((prev) => {
      const newAppliances = [...prev];
      newAppliances[index] = { ...newAppliances[index], [field]: value };
      return newAppliances;
    });
  }, []);

  const addAppliance = useCallback(() => {
    setAppliances((prev) => [...prev, { name: "", watt: "", hours: "", qty: "" }]);
  }, []);

  const removeAppliance = useCallback((index) => {
    setAppliances((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSlide = useCallback((direction) => {
    setCardIndex((prev) => {
      return direction === "left"
        ? Math.max(prev - 1, 0)
        : Math.min(prev + 1, totalCards - 1);
    });
  }, []);

  const calculate = useCallback(() => {
    const sunlight = parseFloat(psh);
    const panel = parseFloat(panelWatt);

    if (!sunlight || !panel) {
      toast.error("Please provide sun hours and panel size.");
      return;
    }
    if (sunlight <= 0) {
      toast.error("Peak Sun Hours must be greater than zero.");
      return;
    }

    let dailyEnergyBudgetWh = 0;
    let details = {};

    if (method === "battery") {
      const count = parseFloat(batteryCount);
      const days = autonomyChoice === "custom"
        ? parseFloat(customAutonomyDays)
        : parseFloat(autonomyChoice);

      if (!count || !days) {
        toast.error("Please complete battery and autonomy fields.");
        return;
      }

      const usableFraction = batteryType === "lead" ? 1.0 : 0.9;
      let totalBatteryWh = 0;
      let voltage = null;
      let ah = null;
      let kwh = null;

      if (batteryType === "lead") {
        voltage = parseFloat(batteryVoltage);
        ah = parseFloat(batteryAh);
        if (!voltage || !ah) {
          toast.error("Please provide voltage and Ah for lead-acid batteries.");
          return;
        }
        totalBatteryWh = count * voltage * ah;
      } else {
        kwh = parseFloat(batteryKwh);
        if (!kwh) {
          toast.error("Please select lithium battery capacity in kWh.");
          return;
        }
        totalBatteryWh = count * kwh * 1000;
      }

      const usableBatteryWh = totalBatteryWh * usableFraction;
      dailyEnergyBudgetWh = usableBatteryWh / days;

      details = {
        method,
        batteryType,
        count,
        voltage,
        ah,
        kwh,
        usableFraction,
        totalBatteryWh,
        usableBatteryWh,
        days,
      };
    } else if (method === "load") {
      const computedAppliances = [];
      let totalLoad = 0;

      appliances.forEach((appliance) => {
        const watts = parseFloat(appliance.watt) || 0;
        const hours = parseFloat(appliance.hours) || 0;
        const qty = parseFloat(appliance.qty) || 0;
        const dailyWh = watts * hours * qty;
        if (dailyWh > 0) {
          computedAppliances.push({
            name: appliance.name || "Appliance",
            watts,
            hours,
            qty,
            dailyWh,
          });
          totalLoad += dailyWh;
        }
      });

      if (totalLoad <= 0) {
        toast.error("Please enter at least one valid appliance load.");
        return;
      }

      dailyEnergyBudgetWh = totalLoad;
      details = { method, appliances: computedAppliances, totalLoad };
    }

    const lossFactor = includeLoss ? 1.3 : 1.0;
    const adjustedDailyEnergyWh = dailyEnergyBudgetWh * lossFactor;
    const requiredSolarW = adjustedDailyEnergyWh / sunlight;
    const panelCount = Math.ceil(requiredSolarW / panel);
    const totalWattage = panelCount * panel;

    let safeChargePanelCount = null;
    if (method === "battery" && batteryType === "lithium") {
      const singleBatteryKwh = parseFloat(batteryKwh);
      if (singleBatteryKwh) {
        const totalBatteryKwh = (batteryCount || 1) * singleBatteryKwh;
        const halfCPowerW = (totalBatteryKwh * 1000) * 0.5;
        safeChargePanelCount = Math.ceil(halfCPowerW / panel);
      }
    }

    setResult({
      ...details,
      sunlight,
      panel,
      dailyEnergyBudgetWh,
      adjustedDailyEnergyWh,
      requiredSolarW,
      panelCount,
      totalWattage,
      includeLoss,
      safeChargePanelCount,
    });
    setStep(6);
  }, [psh, panelWatt, method, batteryCount, batteryVoltage, batteryAh, batteryKwh, batteryType, autonomyChoice, customAutonomyDays, appliances, includeLoss]);

  const handleCalculateClick = useCallback(() => {
    setCalcCount((prev) => {
      const newCount = prev + 1;
      if (newCount <= 2) {
        calculate();
      } else {
        if (window.Android && window.Android.showRewardAd) {
          window.Android.showRewardAd();
          window.rewardAdCompleted = (status) => {
            if (status === true || status === "true" || status === "completed" || status === "success") {
              calculate();
            } else {
              toast.error("Please watch the full ad to view your result.");
            }
          };
        } else {
          calculate();
        }
      }
      return newCount;
    });
  }, [calculate]);

  return (
    <div className="panel-wrapper">
      <section className="hero-section">
        <section className="carousel-container">
          <div className="carousel-wrapper" ref={carouselRef}>
            <div className="carousel-cardpanel">
              <img src="/images/panelicon.png" alt="" className="card-iconpanel" loading="lazy" decoding="async" />
              <div className="card-contentpanel">
                <h3>Courses</h3>
                <p>Learn valuable skills and gain expertise with top-rated courses.</p>
                <a href="/solar-resources" className="card-linkpanel">Explore now →</a>
              </div>
            </div>
            <div className="carousel-cardpanel">
              <img src="/images/panelicon.png" alt="" className="card-iconpanel" loading="lazy" decoding="async" />
              <div className="card-contentpanel">
                <h3>Consultation</h3>
                <p>Get personalized help and advice from our experts.</p>
                <a href="https://wa.me/2348131299300" className="card-linkpanel">Book now →</a>
              </div>
            </div>
            <div className="carousel-cardpanel">
              <img src="/images/panelicon.png" alt="" className="card-iconpanel" loading="lazy" decoding="async" />
              <div className="card-contentpanel">
                <h3>Installers</h3>
                <p>Find trusted installers in your area for any project size.</p>
                <a href="https://wa.me/2348131299300" className="card-linkpanel">Find now →</a>
              </div>
            </div>
          </div>
        </section>
      </section>

      <img src="/images/3.png" alt="" className="card-iconpanelservicecenters" loading="lazy" decoding="async" />

      <div className="panel-sizing-container">
        <h2 className="panel-titlepanel">🌞 Solar Panel Calculator/Sizing</h2>
        <p className="panel-subtitlepanel">Answer a few simple questions.</p>
      </div>

      {step === 1 && (
        <div className="panel-form">
          <h3>Step 1: Choose Calculation Method</h3>
          <p>Would you like to size based on your battery bank or your daily load?</p>
          <label>
            <input type="radio" name="method" value="battery" checked={method === "battery"} onChange={() => setMethod("battery")} /> Battery-Based
          </label>
          <br />
          <label>
            <input type="radio" name="method" value="load" checked={method === "load"} onChange={() => setMethod("load")} /> Load-Based
          </label>
          <button onClick={() => setStep(2)}>Next →</button>
        </div>
      )}

      {step === 2 && method === "battery" && (
        <div className="panel-form">
          <h3>Step 2: Your Battery Setup</h3>
          <label>How many batteries do you have?<Tooltip text="Enter the total number of batteries in your system (1–20)." /></label>
          <select value={batteryCount} onChange={(e) => setBatteryCount(e.target.value)}>
            <option value="">Select number</option>
            {batteryCountOptions.map((n) => (<option key={n} value={n}>{n}</option>))}
          </select>

          <p style={{ fontWeight: "bold", marginBottom: "6px" }}>Battery Type:</p>
          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input type="radio" name="batteryType" value="lead" checked={batteryType === "lead"} onChange={() => setBatteryType("lead")} style={{ marginRight: "8px" }} />
              Lead-acid (about 50% usable)
            </label>
            <label style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
              <input type="radio" name="batteryType" value="lithium" checked={batteryType === "lithium"} onChange={() => setBatteryType("lithium")} style={{ marginRight: "8px" }} />
              Lithium (about 90% usable)
            </label>
          </div>

          {batteryType === "lead" && (
            <>
              <label>Battery Voltage<Tooltip text="Most common batteries are 12V. Larger systems may use 24V or 48V." /></label>
              <select value={batteryVoltage} onChange={(e) => setBatteryVoltage(e.target.value)}>
                <option value="">Select voltage</option>
                <option value="12">12V</option>
                <option value="24">24V</option>
                <option value="48">48V</option>
              </select>
              <label>Battery Capacity (Ah)<Tooltip text="The capacity in amp-hours (Ah) is written on your battery label (e.g. 200Ah)." /></label>
              <select value={batteryAh} onChange={(e) => setBatteryAh(e.target.value)}>
                <option value="">Select capacity</option>
                <option value="100">100 Ah</option>
                <option value="150">150 Ah</option>
                <option value="200">200 Ah</option>
                <option value="220">220 Ah</option>
                <option value="250">250 Ah</option>
                <option value="300">300 Ah</option>
              </select>
            </>
          )}

          {batteryType === "lithium" && (
            <>
              <label>Battery Capacity (kWh)<Tooltip text="Lithium batteries are usually rated in kilowatt-hours (e.g. 5 kWh, 10 kWh)." /></label>
              <select value={batteryKwh} onChange={(e) => setBatteryKwh(e.target.value)}>
                <option value="">Select capacity</option>
                {lithiumKwhOptions.map((size) => (<option key={size} value={size}>{size} kWh</option>))}
              </select>
            </>
          )}

          <label>Backup Days (Autonomy)<Tooltip text="How many days should the batteries supply the load without sun?" /></label>
          <select value={autonomyChoice} onChange={(e) => setAutonomyChoice(e.target.value)}>
            <option value="">Select</option>
            <option value="1">1 day</option>
            <option value="2">2 days</option>
            <option value="3">3 days</option>
            <option value="custom">Custom…</option>
          </select>

          {autonomyChoice === "custom" && (
            <input type="number" placeholder="Enter number of days" value={customAutonomyDays} onChange={(e) => setCustomAutonomyDays(e.target.value)} min="0.5" step="0.5" />
          )}

          <button onClick={() => setStep(1)}>← Back</button>
          <button onClick={() => setStep(3)}>Next →</button>
        </div>
      )}

      {step === 2 && method === "load" && (
        <div className="panel-form">
          <h3>Step 2: Add Your Appliances</h3>
          {appliances.map((appliance, index) => (
            <div key={index} style={{ marginBottom: "12px", borderBottom: "1px solid #ddd", paddingBottom: "8px", display: "flex", flexDirection: "column", width: "100%" }}>
              <input type="text" placeholder="Appliance name" value={appliance.name} onChange={(e) => updateAppliance(index, "name", e.target.value)} style={{ marginBottom: "6px", width: "100%" }} />
              <input type="number" placeholder="Watts" value={appliance.watt} onChange={(e) => updateAppliance(index, "watt", e.target.value)} style={{ marginBottom: "6px", width: "100%" }} />
              <input type="number" placeholder="Hours/day" value={appliance.hours} onChange={(e) => updateAppliance(index, "hours", e.target.value)} style={{ marginBottom: "6px", width: "100%" }} />
              <input type="number" placeholder="Quantity" value={appliance.qty} onChange={(e) => updateAppliance(index, "qty", e.target.value)} style={{ marginBottom: "6px", width: "100%" }} />
              {appliances.length > 1 && (
                <button onClick={() => removeAppliance(index)} style={{ alignSelf: "flex-end", background: "#ff4d4d", color: "#fff", border: "none", padding: "6px 12px", borderRadius: "4px", cursor: "pointer" }}>Remove</button>
              )}
            </div>
          ))}
          <div style={{ marginBottom: "8px" }}>
            <button onClick={addAppliance} style={{ marginTop: "6px", background: "#0077cc", color: "white", border: "none", borderRadius: "4px", padding: "8px 12px", cursor: "pointer" }}>+ Add Another Appliance</button>
          </div>
          <div style={{ marginTop: "8px", marginBottom: "12px" }}>
            <strong>Running total (Wh/day):</strong> {runningTotalLoad.toFixed(0)} Wh/day
          </div>

          <label>Backup Days (Autonomy)<Tooltip text="How many days should the batteries supply the load without sun?" /></label>
          <select value={autonomyChoice} onChange={(e) => setAutonomyChoice(e.target.value)} style={{ marginBottom: "10px", display: "block" }}>
            <option value="">Select</option>
            <option value="1">1 day</option>
            <option value="2">2 days</option>
            <option value="3">3 days</option>
            <option value="custom">Custom…</option>
          </select>

          {autonomyChoice === "custom" && (
            <input type="number" placeholder="Enter number of days" value={customAutonomyDays} onChange={(e) => setCustomAutonomyDays(e.target.value)} min="0.5" step="0.5" style={{ marginBottom: "10px", display: "block" }} />
          )}

          <button onClick={() => setStep(1)}>← Back</button>
          <button onClick={() => setStep(3)}>Next →</button>
        </div>
      )}

      {step === 3 && (
        <div className="panel-form">
          <h3>Step 3: Sunlight in Your Area</h3>
          <p>Enter the average good sunlight hours (Peak Sun Hours).</p>
          <p><small>Examples: Nigeria ~ 4–5 hrs, UK ~ 3 hrs, California ~ 5.5 hrs</small></p>
          <input type="number" placeholder="e.g. 5" value={psh} onChange={(e) => setPsh(e.target.value)} min="1" step="0.1" />
          <button onClick={() => setStep(2)}>← Back</button>
          <button onClick={() => setStep(4)}>Next →</button>
        </div>
      )}

      {step === 4 && (
        <div className="panel-form">
          <h3>Step 4: Solar Panel Size</h3>
          <p>Pick the watt rating of panels you plan to use.</p>
          <select value={panelWatt} onChange={(e) => setPanelWatt(e.target.value)}>
            <option value="">Select panel size</option>
            {panelOptions.map((watt) => (<option key={watt} value={watt}>{watt} W</option>))}
          </select>
          <button onClick={() => setStep(3)}>← Back</button>
          <button onClick={() => setStep(5)}>Next →</button>
        </div>
      )}

      {step === 5 && (
        <div className="panel-form">
          <h3>Step 5: Safety Margin</h3>
          <label>
            <input type="checkbox" checked={includeLoss} onChange={() => setIncludeLoss(!includeLoss)} /> Add 30% extra panels to cover cloudy days and system losses (recommended)
          </label>
          <button onClick={() => setStep(4)}>← Back</button>
          <button onClick={handleCalculateClick}>⚡Calculate</button>
        </div>
      )}

      {step === 6 && result && (
        <div className="panel-result">
          <h4>📊 Your Solar Recommendation</h4>
          <div className="result-section">
            <p className="highlight">✅ You will need <strong>{result.panelCount} solar panel(s) of {result.panel}W each</strong> (≈ {result.totalWattage} W total).</p>
            {result.safeChargePanelCount && (
              <p>🔋 However, to keep your lithium battery healthy, and For safe/fast 0.5C charging which is strongly recommended, you will need: <strong>{result.safeChargePanelCount} panel(s) of {result.panel}W</strong></p>
            )}
            {result.safeChargePanelCount && (
              <small className="explain">ℹ️ Why two numbers?<br />- The first shows how many panels cover your daily usage.<br />- The second shows how many panels ensure your battery charges safely and efficiently.<br />👉 For best results, plan with the higher number.</small>
            )}
            <p>⚡ This setup will provide about <strong>{result.dailyEnergyBudgetWh.toFixed(0)} Wh per day</strong> of solar input, given <strong>{result.sunlight}</strong> hours of good sun.</p>
          </div>
          <hr />
          {result.method === "battery" && (
            <div className="result-section">
              <h5>🔋 Your Battery Bank</h5>
              <ul>
                {result.batteryType === "lead" ? (<li>{result.count} × {result.ah}Ah {result.voltage}V (Lead-acid)</li>) : (<li>{result.count} × {result.kwh} kWh (Lithium)</li>)}
                <li>Usable capacity: {result.usableBatteryWh.toFixed(0)} Wh (≈ {Math.round(result.usableFraction * 100)}% usable)</li>
                <li>Backup days chosen: {result.days} day(s)</li>
              </ul>
            </div>
          )}
          {result.method === "load" && (
            <div className="result-section">
              <h5>⚡ Your Appliances</h5>
              <ul>
                {result.appliances.map((a, i) => (<li key={i}>{a.qty} × {a.name || "Unnamed"} ({a.watts}W × {a.hours}h) = {a.dailyWh.toFixed(0)} Wh/day</li>))}
                <li><strong>Total Load: {result.totalLoad.toFixed(0)} Wh/day</strong></li>
              </ul>
            </div>
          )}
          <hr />
          <div className="result-section">
            <h5>☀️ Solar Array Details</h5>
            <ul>
              <li>Daily energy to supply: {result.dailyEnergyBudgetWh.toFixed(0)} Wh/day</li>
              <li>Adjusted with losses: {result.adjustedDailyEnergyWh.toFixed(0)} Wh/day{result.includeLoss ? " (includes +30% margin)" : ""}</li>
              <li>Required solar array: {result.requiredSolarW.toFixed(0)} W</li>
            </ul>
          </div>
          <hr />
          <button onClick={() => setStep(1)}>🔄 Start Again</button>
        </div>
      )}

      <div style={{ height: "80px" }} />
    </div>
  );
}
